import { styled } from '@mui/material'
import { ArrowBackIcon } from './ArrowBackIcon'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos'
import ControlsButton from './ControlsButton'

import React, { useEffect } from 'react'
import FullScreenGallery from './FullScreenGallery'
import Swipeable from '../Swipeable'

function arrayRotate<T> (arr: T[], count: number): T[] {
  const len = arr.length
  count = count % len
  if (count === 0) return [...arr]
  return [...arr.slice(count), ...arr.slice(0, count)]
}

export interface ImageProps {
  src: string
  filter?: string
  overlay?: React.ReactNode
  onClick?: () => void
  notShownPill?: React.ReactNode
}

interface Props {
  images: ImageProps[]
}

const Gallery = styled('section')(({ theme }) => ({
  display: 'grid',
  height: 250,
  marginTop: 16,
  marginBottom: 16,
  width: '100%',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gridTemplateRows: 'repeat(2, 1fr)',
  gridColumnGap: '1rem',
  gridRowGap: '1rem',
  pointer: 'cursor',
  [theme.breakpoints.up('sm')]: {
    height: 482,
  },
}))

const GalleryItem = styled('div')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 8,
  display: 'none',
  cursor: 'pointer',
  ':nth-of-type(1)': {
    gridArea: '1 / 1 / 3 / 13',
    display: 'block',
  },
  ':not(.always-small &)': {
    [theme.breakpoints.up('md')]: {
      ':nth-of-type(1)': {
        gridArea: '1 / 1 / 3 / 9',
      },
      ':nth-of-type(2)': {
        gridArea: '1 / 9 / 2 / 13',
        display: 'block',
      },
      ':nth-of-type(3)': {
        gridArea: '2 / 9 / 3 / 13',
        display: 'block',
      },
    },
  },
}))

const GalleryImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

export const Overlay = styled('div')({
  position: 'absolute',
  zIndex: 1,
  textAlign: 'center',
  top: '50%',
  left: '50%',
  pointerEvents: 'none',
  transform: 'translate(-50%, -50%)',
})

const ControlsWrapper = styled('div')({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'space-between',
  zIndex: 2,
  width: '100%',
  top: '50%',
  transform: 'translate(0, -50%)',
  opacity: 0,
  transition: 'opacity 0.2s',
  pointerEvents: 'none',
  '*:hover > * > &': {
    opacity: 1,
  },
  '@media (pointer: none)': {
    opacity: 1,
  },
})

const PillArea = styled('div')(({ theme }) => ({
  gridArea: '2 / 9 / 3 / 13',
  zIndex: 2,
  display: 'flex',
  alignSelf: 'flex-end',
  justifyContent: 'space-between',
  padding: 8,
  flexDirection: 'row-reverse',
  [theme.breakpoints.down('md')]: {
    gridArea: '1 / 1 / 3 / 13',
  },
}))

const PillButton = styled('button')(({ theme }) => ({
  background: '#FFFFFF',
  borderRadius: 4,
  color: 'inherit',
  font: 'inherit',
  border: 0,
  padding: '4px 10px',
  display: 'inline-block',
  '& .MuiChip-root': { verticalAlign: 'text-top' },
  cursor: 'pointer',
  [theme.breakpoints.up('md')]: {
    '&.hide-md-and-up': {
      display: 'none',
    },
  },
}))

export const ImageGallery = ({ images }: Props): JSX.Element => {
  const [offset, setOffset] = React.useState(0)
  const [modalOpen, setModalOpen] = React.useState(false)
  const rotatedImages = arrayRotate(images, offset)

  const onPrev = (): void => {
    if (offset > 0) {
      setOffset((offset) => offset - 1)
    } else {
      setOffset(images.length - 1)
    }
  }
  const onNext = (): void => {
    if (offset < images.length - 1) {
      setOffset((offset) => offset + 1)
    } else {
      setOffset(0)
    }
  }

  useEffect(() => {
    /* left right arrow keys */
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowLeft') {
        onPrev()
      } else if (e.key === 'ArrowRight') {
        onNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <FullScreenGallery
        images={images}
        open={modalOpen}
        offset={offset}
        setOffset={setOffset}
        onClose={() => setModalOpen(false)}
        onPrev={onPrev}
        onNext={onNext}
      />
      <Swipeable onLeftSwipe={onPrev} onRightSwipe={onNext}>
        <Gallery
          className={images.length < 3 ? 'always-small gallery' : 'gallery'}
        >
          {rotatedImages.map((image, index) => (
            <GalleryItem key={images.indexOf(image)}>
              <GalleryImage
                src={image.src}
                style={{ filter: image.filter }}
                onClick={
                  image.onClick ??
                  (() => {
                    setOffset((prev) => prev + index)
                    setModalOpen(true)
                  })
                }
              />
              {image.overlay && <Overlay>{image.overlay}</Overlay>}
              {index === 0 && images.length > 1 && (
                <ControlsWrapper>
                  <ControlsButton onClick={onPrev}>
                    <ArrowBackIcon />
                  </ControlsButton>
                  <ControlsButton onClick={onNext}>
                    <ArrowForwardIcon />
                  </ControlsButton>
                </ControlsWrapper>
              )}
            </GalleryItem>
          ))}
          <PillArea>
            <PillButton
              onClick={() => setModalOpen(true)}
              aria-label='Open fullscreen gallery'
            >
              {`${Math.abs(offset % images.length) + 1}/${
                images.length
              } photos`}
            </PillButton>
            {rotatedImages.map((image, index) => {
              if (index === 0) return null
              if (!image.notShownPill) return null
              const shouldBeHiddenInMdAndUp = index === 1 || index === 2
              return (
                <PillButton
                  key={index}
                  onClick={image.onClick}
                  className={shouldBeHiddenInMdAndUp ? 'hide-md-and-up' : ''}
                >
                  {image.notShownPill}
                </PillButton>
              )
            })}
          </PillArea>
        </Gallery>
      </Swipeable>
    </>
  )
}
